import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect } from 'react';
import { useSafeLocalStorage, useStorageKey } from '../../../../tools/storage.js';

export type DragAndDropData = {
    id: string;
};

type UseColumnDragProps<TData, TValue> = {
    id: string;
    columns: ColumnDef<TData, TValue>[];
    fixedFirstColumn: boolean;
};

export function useColumnDrag<TData, TValue>({ id, columns, fixedFirstColumn }: UseColumnDragProps<TData, TValue>) {
    const localStorageKey = useStorageKey({
        base: 'resizable-table',
        utility: 'column-order',
        id,
        version: 1,
    });

    const [columnOrder, setColumnOrder] = useSafeLocalStorage<string[]>(localStorageKey, () =>
        columns
            .map((column) => {
                return column.id;
            })
            .filter((column) => {
                return column !== undefined;
            })
    );

    const resetColumnOrder = useCallback(() => {
        setColumnOrder(
            columns
                .map((column) => {
                    return column.id;
                })
                .filter((id) => {
                    return id !== undefined;
                })
        );
    }, [columns, setColumnOrder]);

    const reorderColumns = useCallback(
        (sourceId: string, targetId: string) => {
            setColumnOrder((prevOrder: string[]) => {
                // find indices of source and target columns
                const sourceIndex = prevOrder.indexOf(sourceId);
                const targetIndex = prevOrder.indexOf(targetId);

                // return unchanged if source or target not found
                if (sourceIndex === -1 || targetIndex === -1) {
                    return prevOrder;
                }

                // If fixedFirstColumn is true, prevent moving the first column
                if (fixedFirstColumn && columns.length > 0) {
                    const firstColumnId = columns[0].id;
                    // Prevent moving the first column
                    if (sourceId === firstColumnId) {
                        return prevOrder;
                    }
                    // Prevent inserting any column before the first column
                    if (targetId === firstColumnId) {
                        // Remove sourceId from its current position
                        const newOrder = prevOrder.filter((id) => id !== sourceId);
                        // Insert sourceId after the first column
                        newOrder.splice(1, 0, sourceId);
                        return newOrder;
                    }
                }

                // create new array without the source column
                const newOrder = prevOrder.filter((id) => id !== sourceId);

                // insert source column at correct position relative to target
                newOrder.splice(targetIndex, 0, sourceId);

                return newOrder;
            });
        },
        [columns, fixedFirstColumn, setColumnOrder]
    );

    useEffect(() => {
        return monitorForElements({
            onDrop: async ({ source, location }) => {
                const target = location.current.dropTargets.at(0);

                if (!target) {
                    return;
                }

                const sourceData = source.data as DragAndDropData;
                const targetData = target.data as DragAndDropData;

                reorderColumns(sourceData.id, targetData.id);
            },
        });
    }, [reorderColumns]);

    return {
        columnOrder,
        setColumnOrder,
        resetColumnOrder,
    };
}
